/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import type { GradientFromId, HostContext, Logger, RGBColor, ResultItem, RootState } from '../../../@types';
import SearchUtils from '../../../utils/SearchUtils';
import './SearchDropdownFile.scss';
import AssetClickBase from '../../../containers/SearchResultsPage/YourWork/AssetClickBase';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import GlobalsUtils from '../../../utils/GlobalsUtils';
import ProjectsService from '../../../services/ProjectsService';

type OwnProps = {
	logger: Logger;
	file: ResultItem;
	index: number;
	variant: 'presearch' | 'autocomplete';
};

type State = {
	renditionSrc: string;
	projectGradient?: {
		primaryColor: RGBColor;
		secondaryColor: RGBColor;
	};
};

/*
 * This renders the line item (App) in the Apps section in the Search Dropdown.
 */
type SearchDropdownFileProps = OwnProps & WrappedComponentProps & PropsFromRedux;
class SearchDropdownFile extends AssetClickBase<SearchDropdownFileProps, State> {
	defaultGradientValue = {
		primaryColor: { r: 0, g: 0, b: 0 }, // #000 - Black
		secondaryColor: { r: 255, g: 255, b: 255 }, // #FFF - White
	};
	state: State = {
		renditionSrc: '',
		projectGradient: this.defaultGradientValue,
	};
	searchHostContext: HostContext = SearchHostContext.getContext();
	analytics: SearchAnalytics = new SearchAnalytics(this.searchHostContext);
	private _isMounted = false;

	/**
	 * Fetches gradient for project cards
	 */
	async fetchProjectGradient(resultItem: ResultItem): Promise<void> {
		const projectsService = new ProjectsService(this.searchHostContext);

		try {
			const gradientsData = await projectsService.fetchGradients();

			// Get gradient ID from resultItem or use DEFAULT
			const data = resultItem.attributes?.data as GradientFromId | undefined;
			const gradientId = data?.gradientId || 'DEFAULT';
			const gradient = gradientsData.get(gradientId);

			if (gradient && this._isMounted) {
				this.setState({
					projectGradient: gradient,
				});
			}
		} catch (error) {
			// On error, DEFAULT gradient will be used (already fetched by ProjectsService)
			this.props.logger.warn(
				'SearchDropdownFile',
				'Failed to fetch project gradients. Using default black/white colors.',
				error,
			);
			if (this._isMounted) {
				this.setState({
					projectGradient: this.defaultGradientValue,
				});
			}
		}
	}

	componentDidMount() {
		this._isMounted = true;

		// Fetch project gradient if this is a project
		if (this.props.file.attributes.assetCategory === 'project') {
			this.fetchProjectGradient(this.props.file);
		}

		const { name, imageRenditionUrl, videoRenditionUrl } = this.props.file.attributes;
		Boolean(imageRenditionUrl || videoRenditionUrl) &&
			SearchUtils.getAssetRendition(imageRenditionUrl || videoRenditionUrl)
				.then((renditionSrc) => {
					if (this._isMounted) {
						this.setState({
							renditionSrc,
						});
					}
				})
				.catch(() => {
					this.props.logger.warn('SearchDropdownFile', `rendition error ${name}`);
				});

		this.analytics.postDropdownFileRender({
			category: this.props.variant === 'presearch' ? 'preSearchRecommendation' : 'AutoComplete',
			file: this.props.file,
			index: this.props.index,
			eventValue: this.props.variant === 'autocomplete' ? 'Files' : 'RecentFiles',
		});
	}

	componentWillUnmount() {
		this._isMounted = false;
	}

	openClickHandler(event: any, asset: ResultItem) {
		this.onClick(this.searchHostContext, asset, { analytics: false });
		this.analytics.postDropdownFileSelection({
			category: this.props.variant === 'presearch' ? 'preSearchRecommendation' : 'AutoComplete',
			file: this.props.file,
			index: this.props.index,
			eventValue: this.props.variant === 'autocomplete' ? 'Files' : 'RecentFiles',
		});
		event.stopPropagation();
	}

	locateClickHandler(event: any, asset: ResultItem) {
		this.genericOnClick(this.searchHostContext, asset, 'locate');
		event.stopPropagation();
	}

	renderFile(): React.ReactElement {
		const { name } = this.props.file.attributes;
		const fileName = GlobalsUtils.removeExtensionFromString(name);
		const fileDescription = SearchUtils.getFileType(this.props.file, this.props);
		const fileElement: React.ReactElement = (
			<>
				<div className='search-dropdown-recentfile-text-container'>
					<div className='search-dropdown-recentfile-text-title'>
						<span>{fileName}</span>
					</div>
					<div className='search-dropdown-recentfile-text-type'>
						<span>{fileDescription}</span>
					</div>
				</div>
				<div className='search-dropdown-recentfile-options-container'>
					<span
						className='search-dropdown-recentfile-text-launch-button'
						onClick={(e) => this.openClickHandler(e, this.props.file)}
					>
						<FormattedMessage id='app.search.yourwork.spillover.open' />
					</span>
					{/* <span
						className='search-dropdown-recentfile-text-launch-button'
						onClick={(e) => this.locateClickHandler(e, this.props.file)}
					>
						<FormattedMessage id='app.search.yourwork.spillover.locate' />
					</span> */}
				</div>
			</>
		);
		return fileElement;
	}

	renderFileRendition(): React.ReactElement {
		const { attributes } = this.props?.file ?? {};
		const { name, mimeType, assetCategory } = attributes;
		let { renditionSrc } = this.state;
		let borderClassName = 'asde-file-border';
		if (!renditionSrc) {
			borderClassName = '';
			renditionSrc = SearchUtils.getDefaultThumbnailURI(this.props.theme, mimeType, name, assetCategory);
		}
		if (assetCategory === 'project') {
			const initial = name.trim().charAt(0).toUpperCase() || '';

			// Calculate background and text colors from gradient data
			let backgroundStyle: React.CSSProperties = {};
			let textStyle: React.CSSProperties = {};

			if (this.state.projectGradient) {
				const { primaryColor, secondaryColor } = this.state.projectGradient;
				const isLightTheme = this.props.theme === 'light';

				// Reverse colors in dark theme
				const bgColor = isLightTheme ? primaryColor : secondaryColor;
				const textColor = isLightTheme ? secondaryColor : primaryColor;

				backgroundStyle = {
					backgroundColor: `rgb(${bgColor.r}, ${bgColor.g}, ${bgColor.b})`,
				};
				textStyle = {
					color: `rgb(${textColor.r}, ${textColor.g}, ${textColor.b})`,
				};
			}

			return (
				<div
					aria-hidden='true'
					className={`search-dropdown-recentfile-icon search-dropdown-project-placeholder ${borderClassName}`}
					style={backgroundStyle}
				>
					<span style={textStyle}>{initial}</span>
				</div>
			);
		}
		const fileRenditionElement: React.ReactElement = (
			<img
				alt={`Filename: ${name}`}
				aria-hidden='true'
				className={`search-dropdown-recentfile-icon ${borderClassName}`}
				src={renditionSrc}
			/>
		);
		return fileRenditionElement;
	}

	render() {
		const { attributes, id } = this.props?.file ?? {};
		const { name } = attributes;
		return (
			<div
				className='search-dropdown-recentfile'
				id={`search-dropdown-recentfile-${id}-${name}`}
				role='option'
				tabIndex={-1}
				onClick={(e) => this.openClickHandler(e, this.props.file)}
			>
				<div className='search-dropdown-recentfile-container'>
					<div className='search-dropdown-recentfile-icon-container'>{this.renderFileRendition()}</div>
					<div className='search-dropdown-recentfile-details-container'>{this.renderFile()}</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownFile));
